import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { MapPin, Plane } from 'lucide-react';

const Zone = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const cities = t('zone.cities');

  return (
    <section
      id="zone"
      data-testid="zone-section"
      className={`relative py-24 md:py-32 overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0A0A0A]' : 'bg-[#FAF7F0]'
      }`}
    >
      {/* Subtle background pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, ${isDark ? 'rgba(212,175,55,0.4)' : 'rgba(212,175,55,0.5)'} 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Decorative golden glow */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full blur-[150px] pointer-events-none ${
        isDark ? 'bg-[#D4AF37]/5' : 'bg-[#D4AF37]/8'
      }`} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Title */}
        <div className="text-center mb-16 md:mb-20">
          <h2 
            data-testid="zone-title"
            className={`font-playfair text-3xl sm:text-4xl lg:text-5xl mb-6 ${
              isDark ? 'text-white' : 'text-[#1A1A1A]'
            }`}
          >
            {t('zone.title')}
          </h2>
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
            <div className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
          </div>
          <p className={`max-w-2xl mx-auto text-base md:text-lg ${
            isDark ? 'text-[#F8F5EE]/70' : 'text-[#1A1A1A]/70'
          }`}>
            {t('zone.description')}
          </p>
        </div>

        {/* Cities Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 mb-12">
          {Array.isArray(cities) && cities.map((city, index) => (
            <div
              key={index}
              data-testid={`zone-city-${index}`}
              className={`group flex flex-col items-center p-6 rounded-sm transition-all duration-400 border ${
                isDark 
                  ? 'bg-gradient-to-b from-[#1A1A1A] to-[#141414] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 hover:shadow-[0_10px_40px_rgba(212,175,55,0.1)]' 
                  : 'bg-white border-[#D4AF37]/30 hover:border-[#D4AF37]/60 hover:shadow-[0_10px_40px_rgba(212,175,55,0.15)]'
              } hover:-translate-y-1`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                isDark 
                  ? 'bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20' 
                  : 'bg-[#D4AF37]/15 group-hover:bg-[#D4AF37]/25'
              }`}>
                <MapPin className="w-5 h-5 text-[#D4AF37] group-hover:scale-110 transition-transform duration-300" />
              </div>
              <span className={`text-sm md:text-base text-center font-light ${
                isDark ? 'text-white/90' : 'text-[#1A1A1A]/90'
              }`}>
                {city}
              </span>
            </div>
          ))}
        </div>

        {/* Airport Transfer Card */}
        <div 
          data-testid="zone-airport"
          className={`flex items-center justify-center gap-4 p-6 md:p-8 rounded-sm border-2 transition-all duration-400 ${
            isDark 
              ? 'bg-gradient-to-r from-[#1A1A1A] via-[#141414] to-[#1A1A1A] border-[#D4AF37]/30 hover:border-[#D4AF37]/60 hover:shadow-[0_0_50px_rgba(212,175,55,0.1)]' 
              : 'bg-gradient-to-r from-white via-[#F8F5EE] to-white border-[#D4AF37]/40 hover:border-[#D4AF37]/70 hover:shadow-[0_0_50px_rgba(212,175,55,0.15)]'
          }`}
        >
          <div className={`flex items-center justify-center w-14 h-14 rounded-full ${
            isDark ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/30' : 'bg-[#D4AF37]/20 border border-[#D4AF37]/40'
          }`}>
            <Plane className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <span className={`text-base md:text-lg font-light ${
            isDark ? 'text-white' : 'text-[#1A1A1A]'
          }`}>
            {t('zone.airport')}
          </span>
        </div>
      </div>

      {/* Bottom wave separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 40" className="w-full" preserveAspectRatio="none">
          <path
            d="M0,40 L0,20 Q720,40 1440,20 L1440,40 Z"
            fill={isDark ? '#0D0D0D' : '#F8F5EE'}
            className="transition-colors duration-500"
          />
          <path
            d="M0,20 Q720,40 1440,20"
            fill="none"
            stroke="#D4AF37"
            strokeWidth="1"
            strokeOpacity="0.2"
          />
        </svg>
      </div>
    </section>
  );
};

export default Zone;
